<template>
    <BaseModal
        v-if="props.showModal"
        title="Bulk Student Enrollment"
        :loading="isSubmitting"
        max-width="7xl"
        :show-footer="false"
        @close="$emit('close')"
    >
        <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- Class and Semester Selection -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Class Selection -->
                <div>
                    <SearchableSelect
                        v-model="form.class_id"
                        :options="classOptions"
                        label="Class"
                        placeholder="Select a class"
                        :optionLabel="
                            (classOption) =>
                                `${classOption.grade_level} - ${classOption.name}`
                        "
                        optionValue="id"
                        :required="true"
                        :error="errors.class_id"
                    />
                </div>

                <!-- Semester Selection -->
                <div>
                    <SearchableSelect
                        v-model="form.semester_id"
                        :options="semesterOptions"
                        label="Semester"
                        placeholder="Select a semester"
                        :optionLabel="
                            (semester) =>
                                `${semester.name} (${semester.academic_year?.name})`
                        "
                        optionValue="id"
                        :required="true"
                        :error="errors.semester_id"
                    />
                </div>
            </div>

            <!-- Enrollment Date and Status -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Enrollment Date -->
                <div>
                    <label
                        for="enrollment_date"
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                        Enrollment Date <span class="text-danger-500">*</span>
                    </label>
                    <input
                        id="enrollment_date"
                        v-model="form.enrollment_date"
                        type="date"
                        required
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    />
                    <div
                        v-if="errors.enrollment_date"
                        class="text-danger-500 text-sm mt-1"
                    >
                        {{ errors.enrollment_date }}
                    </div>
                </div>

                <!-- Active Status -->
                <div class="flex items-center pt-6">
                    <input
                        id="is_active"
                        v-model="form.is_active"
                        type="checkbox"
                        class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-700 dark:text-white"
                    />
                    <label
                        for="is_active"
                        class="ml-2 block text-sm text-gray-900 dark:text-white"
                    >
                        Active Enrollment
                    </label>
                </div>
            </div>

            <!-- Student Selection Tabs -->
            <div class="space-y-4">
                <div class="border-b border-gray-200 dark:border-gray-700">
                    <nav class="-mb-px flex space-x-8">
                        <button
                            type="button"
                            @click="switchTab('select')"
                            :class="[
                                activeTab === 'select'
                                    ? 'border-primary-500 text-primary-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm',
                            ]"
                        >
                            Select Students
                        </button>
                    </nav>
                </div>

                <!-- Select Students Tab -->
                <div v-if="activeTab === 'select'" class="space-y-4">
                    <!-- Search and Filter -->
                    <div class="flex flex-col sm:flex-row gap-4">
                        <div class="flex-1">
                            <input
                                v-model="studentSearchQuery"
                                @input="filterStudents"
                                type="text"
                                placeholder="Search students by name or ID..."
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                        <div class="flex gap-2">
                            <button
                                type="button"
                                @click="selectAllVisible"
                                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md"
                            >
                                Select All Visible
                            </button>
                            <button
                                type="button"
                                @click="clearSelection"
                                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md"
                            >
                                Clear Selection
                            </button>
                        </div>
                    </div>

                    <!-- Selected Students Count -->
                    <div class="text-sm text-gray-600 dark:text-gray-400">
                        {{ selectedStudentIds.length }} student(s) selected
                    </div>

                    <!-- Students List -->
                    <div
                        class="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md"
                    >
                        <div
                            v-for="student in filteredStudents"
                            :key="student.id"
                            class="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                        >
                            <input
                                type="checkbox"
                                :id="`student-${student.id}`"
                                :value="student.id"
                                v-model="selectedStudentIds"
                                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-700 dark:text-white"
                            />
                            <label
                                :for="`student-${student.id}`"
                                class="ml-3 flex-1 flex items-center cursor-pointer"
                            >
                                <div>
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ student.user?.name }}
                                    </div>
                                    <div
                                        class="text-xs text-gray-500 dark:text-gray-400"
                                    >
                                        ID: {{ student.student_id }}
                                    </div>
                                </div>
                            </label>
                        </div>
                        <div
                            v-if="filteredStudents.length === 0"
                            class="p-4 text-center text-gray-500 dark:text-gray-400"
                        >
                            No students found
                        </div>
                    </div>
                </div>

                <!-- Upload File Tab -->
                <div v-if="activeTab === 'upload'" class="space-y-4">
                    <div
                        class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center"
                    >
                        <Upload class="mx-auto h-12 w-12 text-gray-400" />
                        <div class="mt-4">
                            <label for="file-upload" class="cursor-pointer">
                                <span
                                    class="mt-2 block text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    Upload Excel file
                                </span>
                                <input
                                    id="file-upload"
                                    name="file-upload"
                                    type="file"
                                    accept=".xlsx,.xls"
                                    class="sr-only"
                                    @change="handleFileUpload"
                                />
                                <p
                                    class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                                >
                                    XLSX or XLS format only
                                </p>
                            </label>
                        </div>
                        <p
                            class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                        >
                            File should contain student enrollment data
                        </p>
                    </div>

                    <!-- File Info -->
                    <div
                        v-if="uploadedFile"
                        class="bg-gray-50 dark:bg-gray-700 p-4 rounded-md"
                    >
                        <div class="flex items-center justify-between">
                            <div class="flex items-center">
                                <Check class="h-5 w-5 text-success-500 mr-2" />
                                <span
                                    class="text-sm text-gray-700 dark:text-gray-300"
                                >
                                    {{ uploadedFile.name }}
                                </span>
                            </div>
                            <button
                                type="button"
                                @click="removeFile"
                                class="text-danger-500 hover:text-danger-700"
                            >
                                <X class="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <!-- Upload Instructions -->
                    <div
                        class="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-md"
                    >
                        <h4
                            class="text-sm font-medium text-primary-800 dark:text-primary-200 mb-2"
                        >
                            File Format Instructions:
                        </h4>
                        <ul
                            class="text-xs text-primary-700 dark:text-primary-300 space-y-1"
                        >
                            <li>\u2022 Excel file (.xlsx or .xls)</li>
                            <li>\u2022 First row should contain headers:</li>
                            <li class="ml-4">
                                #, NAMA, NISN, TANGGAL MULAI(YYYY-MM-DD), KODE
                                KELAS, KODE SEMESTER, STATUS
                            </li>
                            <li>\u2022 One student enrollment per row</li>
                            <li>\u2022 STATUS: true/false or 1/0</li>
                        </ul>
                    </div>

                    <!-- Download Template Button -->
                    <div
                        class="bg-success-50 dark:bg-success-900/20 p-4 rounded-md"
                    >
                        <h4
                            class="text-sm font-medium text-success-800 dark:text-success-200 mb-2"
                        >
                            Need a template?
                        </h4>
                        <p
                            class="text-xs text-success-700 dark:text-success-300 mb-3"
                        >
                            Download the Excel template to see the required
                            format
                        </p>
                        <button
                            type="button"
                            @click="downloadTemplate"
                            class="inline-flex items-center px-3 py-2 border border-transparent text-xs font-medium rounded text-success-700 bg-success-100 hover:bg-success-200 dark:bg-success-800 dark:text-success-100 dark:hover:bg-success-700"
                        >
                            <Download class="h-4 w-4 mr-2" />
                            Download Template
                        </button>
                    </div>
                </div>
            </div>

            <!-- Form Actions -->
            <div
                class="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700"
            >
                <button
                    type="button"
                    @click="$emit('close')"
                    class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    :disabled="
                        isSubmitting ||
                        (activeTab === 'select' &&
                            selectedStudentIds.length === 0) ||
                        (activeTab === 'upload' && !uploadedFile)
                    "
                    class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <span v-if="isSubmitting" class="flex items-center">
                        <Loader2
                            class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        />
                        {{
                            activeTab === "upload"
                                ? "Importing..."
                                : `Enrolling ${selectedStudentIds.length} students...`
                        }}
                    </span>
                    <span v-else>
                        {{
                            activeTab === "upload"
                                ? "Import from Excel"
                                : `Enroll ${selectedStudentIds.length} student(s)`
                        }}
                    </span>
                </button>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from "vue";
import notificationService from "@/utils/notifications";
import { studentApi, type Student } from "@/api/student";
import { classSchoollApi, type ClassSchooll } from "@/api/classSchooll";
import semesterApi, { type Semester } from "@/api/semester";
import studentClassApi from "@/api/studentClass";
import { type ParsedStudent } from "@/utils/fileParser";
import { X, Upload, Check, Download, Loader2 } from "@lucide/vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BaseModal from "@/components/BaseModal.vue";

interface Props {
    showModal: boolean;
}

const props = defineProps<Props>();

interface Emits {
    close: [];
    saved: [result: any];
}

const emit = defineEmits<Emits>();
import { useStudentClassStore } from "@/stores/studentClassStore";

const studentClassStore = useStudentClassStore();

const isSubmitting = ref(false);
const errors = ref<Record<string, string>>({});
const studentOptions = ref<Student[]>([]);
const classOptions = ref<ClassSchooll[]>([]);
const semesterOptions = ref<Semester[]>([]);
const selectedStudentIds = ref<string[]>([]);
const studentSearchQuery = ref("");
const activeTab = ref<"select" | "upload">("select");
const uploadedFile = ref<File | null>(null);

const form = ref({
    class_id: "",
    semester_id: "",
    enrollment_date: new Date().toISOString().split("T")[0],
    is_active: true,
});

const filteredStudents = computed(() => {
    if (!studentSearchQuery.value) {
        return studentOptions.value;
    }

    const query = studentSearchQuery.value.toLowerCase();
    return studentOptions.value.filter((student) => {
        return (
            student.user?.name?.toLowerCase().includes(query) ||
            student.student_id?.toLowerCase().includes(query) ||
            student.user?.email?.toLowerCase().includes(query)
        );
    });
});

const fetchOptions = async () => {
    try {
        const [studentsResponse, classesResponse, semestersResponse] =
            await Promise.all([
                studentApi.getAll({ per_page: 1000 }),
                classSchoollApi.getAll({ per_page: 1000 }),
                semesterApi.getSemesters({ per_page: 1000 }),
            ]);
        studentOptions.value = studentsResponse.data?.items || [];
        classOptions.value = classesResponse.data?.items || [];
        semesterOptions.value = semestersResponse.data?.items || [];
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const filterStudents = () => {
    // Reactive via computed
};

const selectAllVisible = () => {
    const visibleIds = filteredStudents.value.map((student) => student.id);
    selectedStudentIds.value = [
        ...new Set([...selectedStudentIds.value, ...visibleIds]),
    ];
};

const clearSelection = () => {
    selectedStudentIds.value = [];
};

const handleFileUpload = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
        uploadedFile.value = target.files[0];
    }
};

const switchTab = (tab: "select" | "upload") => {
    activeTab.value = tab;
    if (tab === "upload") {
        selectedStudentIds.value = [];
    } else {
        uploadedFile.value = null;
    }
};

const downloadTemplate = () => {
    const templateUrl = "/template-excel/TEMPLATE_IMPORT_STUDENT_CLASS.xlsx";
    const link = document.createElement("a");
    link.href = templateUrl;
    link.download = "TEMPLATE_IMPORT_STUDENT_CLASS.xlsx";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const removeFile = () => {
    uploadedFile.value = null;
    const fileInput = document.getElementById(
        "file-upload",
    ) as HTMLInputElement;
    if (fileInput) {
        fileInput.value = "";
    }
};

const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.value.class_id) {
        newErrors.class_id = "Class is required";
    }

    if (!form.value.semester_id) {
        newErrors.semester_id = "Semester is required";
    }

    if (!form.value.enrollment_date) {
        newErrors.enrollment_date = "Enrollment date is required";
    }

    if (selectedStudentIds.value.length === 0) {
        newErrors.students = "At least one student must be selected";
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

const handleSubmit = async () => {
    if (activeTab.value === "upload") {
        if (!uploadedFile.value) {
            showNotification("Please select a file to import", "error");
            return;
        }

        isSubmitting.value = true;
        errors.value = {};

        try {
            const response = await studentClassApi.import(uploadedFile.value);

            if (response.success) {
                const successMessage = `Successfully imported ${response.data?.length || 0} student class enrollments`;
                showNotification(successMessage, "success");
                resetForm();
                emit("close");
            } else {
                errors.value = {
                    submit:
                        response.message || "Import failed. Please try again.",
                };
                showNotification(
                    response.message || "Import failed. Please try again.",
                    "error",
                );
            }
        } catch (error: any) {
            console.error("Import error:", error);
            isSubmitting.value = false;

            if (error.response?.data?.data) {
                const errorData = error.response.data.data;
                if (Array.isArray(errorData) && errorData.length > 0) {
                    const errorMessages = errorData
                        .map((err: any) => {
                            return `Row ${err.lokasi_row} - ${err.lokasi_column}: ${err.errors.join(", ")}`;
                        })
                        .join("\n");
                    showNotification(errorMessages, "error");
                }
            } else if (error.response?.data?.message) {
                errors.value = {
                    submit: error.response.data.message,
                };
                showNotification(error.response.data.message, "error");
            } else {
                errors.value = {
                    submit: "Failed to import student class enrollments",
                };
                showNotification(
                    "Failed to import student class enrollments",
                    "error",
                );
            }
        } finally {
            isSubmitting.value = false;
        }
    } else {
        if (!validateForm()) {
            return;
        }

        isSubmitting.value = true;
        errors.value = {};

        try {
            const bulkData = {
                class_id: form.value.class_id,
                semester_id: form.value.semester_id,
                enrollment_date: form.value.enrollment_date,
                is_active: form.value.is_active,
                student_ids: selectedStudentIds.value,
            };

            const result =
                await studentClassStore.bulkCreateStudentClasses(bulkData);

            if (result) {
                const successMessage = `Successfully enrolled ${result.success_count} out of ${result.total_count} students`;
                if (result.error_count > 0) {
                    const errorDetails = result.errors.slice(0, 5).join("\n");
                    const moreErrors =
                        result.errors.length > 5
                            ? `\n... and ${result.errors.length - 5} more errors`
                            : "";

                    showNotification(
                        `${successMessage}.\n\nErrors:\n${errorDetails}${moreErrors}`,
                        "warning",
                    );
                } else {
                    showNotification(successMessage, "success");
                }
                resetForm();
                emit("close");
            }
        } catch (error: any) {
            console.error("Form submission error:", error);
            isSubmitting.value = false;

            if (error.response?.data?.errors) {
                const apiErrors = error.response.data.errors;
                for (const [key, messages] of Object.entries(apiErrors)) {
                    (errors.value as any)[key] = Array.isArray(messages)
                        ? (messages as string[])[0]
                        : String(messages);
                }

                if (error.response.data.errors.unique_enrollments) {
                    showNotification(
                        error.response.data.errors.unique_enrollments,
                        "error",
                    );
                } else {
                    const errorMessages = Object.values(
                        error.response.data.errors,
                    ).join("\n");
                    showNotification(errorMessages, "error");
                }
            } else if (error.response?.data?.message) {
                errors.value = {
                    submit: error.response.data.message,
                };
                showNotification(error.response.data.message, "error");
            } else {
                errors.value = {
                    submit: "Failed to enroll students",
                };
                showNotification("Failed to enroll students", "error");
            }
        } finally {
            isSubmitting.value = false;
        }
    }
};

const resetForm = () => {
    form.value = {
        class_id: "",
        semester_id: "",
        enrollment_date: new Date().toISOString().split("T")[0],
        is_active: true,
    };
    selectedStudentIds.value = [];
    studentSearchQuery.value = "";
    activeTab.value = "select";
    uploadedFile.value = null;
    errors.value = {};
};

watch(
    () => props.showModal,
    (newVal) => {
        if (newVal) {
            fetchOptions();
        }
    },
);

const showNotification = (
    message: string,
    type: "success" | "error" | "warning" = "success",
) => {
    notificationService[type](message);
};

onMounted(() => {
    if (props.showModal) {
        fetchOptions();
    }
});

watch(
    () => props.showModal,
    (newVal) => {
        if (!newVal) {
            resetForm();
        }
    },
);
</script>
